import { YId } from '../../y/types';
import { uiSetBG } from '../bg';
import { $el } from '../elements';
import { UIViewSpec } from '../view';
import { UICustomElement } from './custom-element';

declare global {
    interface Element {
        ui_field_provider?: (field: string) => string | undefined;
        ui_field_editor?: (field: UIFieldTag) => void;
        ui_fields?: { [field: string]: string };
        ui_data?: { [key: string]: any | undefined };
        ui_data_listener?: { [key: string]: ((value: any) => void) | undefined };
    }
}

@UICustomElement('ui-field')
export class UIFieldTag extends HTMLElement {
    constructor(key?: string, name?: string) {
        super();

        this.setAttribute('translate', 'no');
        if(key)
            this.setAttribute('name', key);
        if(name)
            this.ui_empty($el.SPAN('ui-field-name', `\u00a0\u00a0\u00a0\u00a0\u00a0${name.replaceAll(' ', '\u00a0')}\u00a0\u00a0\u00a0\u00a0\u00a0`));
    }

    connectedCallback() {
        const editor = this.closest('.x-field-editor');

        if(editor) {
            editor.ui_field_editor?.(this);
            return;
        }

        const name = this.getAttribute('name');
        let value: string | undefined;

        if(name) {
            let provider: Element | null | undefined = this.closest('.x-field-provider');

            while(provider) {
                value = provider.ui_field_provider?.(name) ?? provider.ui_fields?.[name];
                if(value)
                    break;
                provider = provider.parentElement?.closest('.x-field-provider');
            }
        }

        this.textContent = value || '';
    }
}

@UICustomElement('ui-image')
export class UIImageTag extends HTMLElement {
    constructor(media?: YId, width?: number, height?: number, aspect?: number) {
        super();

        if(media)
            this.dataset.media = media;
        else
            media = this.dataset.media as YId;

        if(width)
            this.dataset.width = width.toString();
        else
            width = parseInt(this.dataset.width || '0');

        if(height)
            this.dataset.height = height.toString();
        else
            height = parseInt(this.dataset.height || '0')

        if(aspect)
            this.dataset.aspect = aspect.toString();
        else
            aspect = parseFloat(this.dataset.aspect || '0');

        if(media && width && height && aspect) {
            uiSetBG(this, { media, size: '100% 100%' });
            this.style.setProperty('--ui-width', `${width}px`);
            this.style.setProperty('--ui-aspect', `${aspect}`);
        }
    }

    connectedCallback() {
        const editor = this.closest('.x-field-editor');

        if(editor) {
            editor.ui_field_editor?.(this);
            return;
        }
    }
}

export function registerFieldEditor(where: UIViewSpec, editor: (field: UIFieldTag) => void) {
    const elem = where.ui_elem;

    elem.classList.add('x-field-editor');
    elem.ui_field_editor = editor;
}

export function registerFieldProvider(where: UIViewSpec, provider: (field: string) => string | undefined) {
    const elem = where.ui_elem;

    elem.classList.add('x-field-provider');
    elem.ui_field_provider = provider;
}

export function registerField(where: UIViewSpec, key: string, value: string) {
    const elem = where.ui_elem;

    elem.classList.add('x-field-provider');
    elem.ui_fields ??= {};
    elem.ui_fields[key] = value;

    for(const t of elem.querySelectorAll<UIFieldTag>(`ui-field[name="${key}"]`)) {
        t.textContent = value;
    }
}

export function getDataValue(where: UIViewSpec, key: string): any | undefined {
    return where.ui_elem.closest(`.x-data-provider-${key}`)?.ui_data?.[key];
}

export function setDataValue(where: UIViewSpec, key: string, value: any) {
    const elem = where.ui_elem;

    elem.classList.add(`x-data-provider-${key}`);
    elem.ui_data ??= {};
    elem.ui_data[key] = value;

    for(const t of elem.querySelectorAll(`.x-data-listener-${key}`)) {
        t.ui_data_listener?.[key]?.(value);
    }
}

export function registerDataListener(where: UIViewSpec, key: string, listener: (value: any) => void) {
    const elem = where.ui_elem;

    elem.classList.add(`x-data-listener-${key}`);

    if(elem.ui_data_listener)
        elem.ui_data_listener[key] = listener;
    else
        elem.ui_data_listener = { [key]: listener };
}
